<template>
  <div class="home-page">
    <!-- La primera pantalla: la barra y el hero, juntos, ocupando exactamente
         el alto del viewport. Envueltos en vez de darle al hero un
         `calc(100vh - 60px)`, que obliga a repetir a mano el alto de la barra y
         se rompe en cuanto la barra cambia (otro idioma, otra fuente). -->
    <div class="home-first-screen">
      <!-- Top Bar -->
      <div class="home-topbar">
        <div class="home-topbar-left">
          <LogoIcon :size="28" color="#8ab4f8" />
          <span class="home-topbar-title">Meets</span>
        </div>
        <div class="home-topbar-right">
          <!-- El idioma se puede cambiar desde cualquier pantalla, no solo desde
               la sala: quien abre la web por su cuenta (sin el `?lang=` que
               añade Moodle) llega aquí antes que a ninguna otra parte. -->
          <LanguageSelect />
          <span class="home-topbar-time">{{ currentTime }}</span>
        </div>
      </div>

      <!-- Hero Section -->
      <div class="home-hero" @pointermove="moveTilt" @pointerleave="dropTilt">
        <div class="home-hero-content">
          <i18n-t keypath="home.heroTitle" tag="h1" class="home-hero-title">
            <template #break><br /></template>
            <template #highlight><strong><i>{{ $t('home.heroTitleHighlight') }}</i></strong></template>
          </i18n-t>
          <i18n-t keypath="home.heroSubtitle" tag="p" class="home-hero-subtitle">
            <template #hd><strong>{{ $t('home.heroSubtitleHd') }}</strong></template>
          </i18n-t>

          <div class="home-hero-actions">
            <router-link to="/create" class="home-btn-primary">
              <VideoCameraIcon class="home-btn-icon" />
              {{ $t('home.create') }}
            </router-link>

            <router-link to="/join" class="home-btn-secondary">
              <UsersIcon class="home-btn-icon" />
              {{ $t('home.join') }}
            </router-link>
          </div>
        </div>

        <!-- Sobre el logotipo, dos maquetas de lo que hace la plataforma: una
             sesión en directo grabándose y una grabación ya publicada con sus
             subtítulos. Son DECORACIÓN —`aria-hidden`, nada pulsable— así que
             no llevan texto que haya que traducir más allá del «en directo»:
             el resto son iniciales, barras y un tiempo. -->
        <div class="home-hero-visual" ref="heroVisual">
          <LogoIcon class="home-hero-logo" :size="280" color="rgba(138, 180, 248, 0.08)" sheen />

          <div class="home-mock home-mock--live" aria-hidden="true">
            <div class="home-mock-head">
              <span class="home-mock-badge">
                <span class="home-mock-pulse"></span>{{ $t('home.heroLive') }}
              </span>
              <span class="home-mock-rec">REC</span>
            </div>
            <div class="home-mock-tiles">
              <span class="home-mock-tile home-mock-tile--blue">MR</span>
              <span class="home-mock-tile home-mock-tile--green">AL</span>
              <span class="home-mock-tile home-mock-tile--purple">JS</span>
              <span class="home-mock-tile home-mock-tile--amber">+8</span>
            </div>
          </div>

          <div class="home-mock home-mock--screen" aria-hidden="true">
            <div class="home-mock-window">
              <span class="home-mock-window-bar">
                <i></i><i></i><i></i>
              </span>
              <span class="home-mock-window-body">
                <i class="home-mock-line"></i>
                <i class="home-mock-line home-mock-line--short"></i>
                <i class="home-mock-line home-mock-line--tiny"></i>
              </span>
            </div>
            <span class="home-mock-chip home-mock-chip--cyan">
              <ComputerDesktopIcon class="home-mock-chip-icon" />
            </span>
          </div>

          <div class="home-mock home-mock--board" aria-hidden="true">
            <!-- El trazo se dibuja solo cada pocos segundos: es una pizarra, y
                 lo que la explica es ver aparecer la línea. -->
            <svg class="home-mock-board-canvas" viewBox="0 0 120 56" fill="none">
              <!-- pathLength="1" normaliza el largo del trazo: así el mismo
                   par de números del CSS (dasharray/dashoffset 1 → 0) dibuja
                   igual la curva y el círculo, sin medir ninguno de los dos. -->
              <path
                class="home-mock-stroke"
                d="M8 42C18 14 30 48 42 26S62 6 74 24s14 22 38 6"
                pathLength="1"
                stroke="#8ab4f8"
                stroke-width="3"
                stroke-linecap="round"
              />
            </svg>
            <span class="home-mock-chip home-mock-chip--green">
              <PencilIcon class="home-mock-chip-icon" />
            </span>
          </div>

          <!-- La misma frase pasando por los cuatro idiomas: es lo que hace la
               plataforma con los subtítulos en vivo, dicho sin explicarlo. -->
          <div class="home-mock home-mock--caption" aria-hidden="true">
            <Transition name="home-caption" mode="out-in">
              <span :key="caption.lang" class="home-mock-caption-text">{{ caption.text }}</span>
            </Transition>
          </div>

          <div class="home-mock home-mock--rec" aria-hidden="true">
            <span class="home-mock-play">
              <PlayIcon class="home-mock-play-icon" />
            </span>
            <span class="home-mock-lines">
              <i class="home-mock-line"></i>
              <i class="home-mock-line home-mock-line--short"></i>
            </span>
            <span class="home-mock-time">49:24</span>
            <span class="home-mock-langs">
              <i>es</i><i>en</i><i>fr</i><i>it</i>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Grid -->
    <!-- La linterna del cursor: el oyente va en la rejilla, no en cada tarjeta
         (ver moveGlow). Sin JS, la tarjeta se queda como estaba. -->
    <div
      class="home-features"
      @pointermove="moveGlow"
      @pointerleave="dropGlow"
    >
      <div class="home-feature-card">
        <div class="home-feature-icon home-feature-icon--blue">
          <VideoCameraIcon class="home-icon" />
        </div>
        <h3 class="home-feature-title">{{ $t('home.featureHd') }}<sup :data-tooltip="$t('home.featureHdTip')">*</sup></h3>
        <p class="home-feature-desc">{{ $t('home.featureHdDesc') }}</p>
      </div>

      <div class="home-feature-card">
        <div class="home-feature-icon home-feature-icon--green">
          <PencilIcon class="home-icon" />
        </div>
        <h3 class="home-feature-title">{{ $t('home.featureBoard') }}</h3>
        <p class="home-feature-desc">{{ $t('home.featureBoardDesc') }}</p>
      </div>

      <div class="home-feature-card">
        <div class="home-feature-icon home-feature-icon--purple">
          <CpuChipIcon class="home-icon" />
        </div>
        <h3 class="home-feature-title">{{ $t('home.featureAi') }}</h3>
        <p class="home-feature-desc">{{ $t('home.featureAiDesc') }}</p>
      </div>

      <!-- Las tres que estaban en «Características avanzadas»: ya no son un
           apartado aparte, son tres tarjetas más de esta misma rejilla (3 + 3).
           Cada una con su color, porque seis tarjetas en la misma rejilla con
           tres colores se leen como tres parejas que no significan nada. -->
      <div class="home-feature-card">
        <div class="home-feature-icon home-feature-icon--red">
          <FilmIcon class="home-icon" />
        </div>
        <h3 class="home-feature-title">{{ $t('home.featureRecording') }}</h3>
        <p class="home-feature-desc">{{ $t('home.featureRecordingDesc') }}</p>
      </div>

      <div class="home-feature-card">
        <div class="home-feature-icon home-feature-icon--cyan">
          <ChatBubbleLeftRightIcon class="home-icon" />
        </div>
        <h3 class="home-feature-title">{{ $t('home.featureChat') }}</h3>
        <p class="home-feature-desc">{{ $t('home.featureChatDesc') }}</p>
      </div>

      <div class="home-feature-card">
        <div class="home-feature-icon home-feature-icon--amber">
          <UsersIcon class="home-icon" />
        </div>
        <h3 class="home-feature-title">{{ $t('home.featureBreakout') }}</h3>
        <p class="home-feature-desc">{{ $t('home.featureBreakoutDesc') }}</p>
      </div>
    </div>

    <!-- Footer -->
    <div class="home-footer">
      <p>© 2026 Magister v{{ appVersion }}. Hecho con <a href="https://vuejs.org/" target="_blank" rel="noopener noreferrer">Vue</a>.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { currentLocale } from '@/i18n'
import LogoIcon from '@/components/ui/icons/LogoIcon.vue'
import LanguageSelect from '@/components/ui/LanguageSelect.vue'
import {
  VideoCameraIcon,
  UsersIcon,
  PencilIcon,
  CpuChipIcon,
  ChatBubbleLeftRightIcon,
  FilmIcon,
  PlayIcon,
  ComputerDesktopIcon
} from '@heroicons/vue/24/solid'

const currentTime = ref('')
const appVersion = __APP_VERSION__
let clockInterval: ReturnType<typeof setInterval> | null = null

// ── La línea de subtítulo que va cambiando de idioma ───────────────────────
// La misma frase en los cuatro idiomas a los que la plataforma traduce en vivo.
// NO sale de i18n a propósito: no es texto de interfaz, es la demostración de
// que se traduce, así que tiene que pasar por los cuatro sea cual sea el idioma
// de quien mira (con i18n se quedaría clavada en uno y no se vería nada).
const CAPTION_LINES = [
  { lang: 'es', text: 'Buenos días.' },
  { lang: 'en', text: "Good morning." },
  { lang: 'fr', text: 'Bonjour.' },
  { lang: 'it', text: 'Buongiorno.' }
]

// Arranca por el idioma de la interfaz, para que lo primero que se lea se
// entienda, y sigue con los demás en orden.
const captionLines = (() => {
  const here = CAPTION_LINES.findIndex((line) => line.lang === currentLocale())
  return here <= 0 ? CAPTION_LINES : [...CAPTION_LINES.slice(here), ...CAPTION_LINES.slice(0, here)]
})()

const captionStep = ref(0)
const caption = computed(() => captionLines[captionStep.value])
let captionInterval: ReturnType<typeof setInterval> | null = null

const updateClock = () => {
  const now = new Date()
  currentTime.value = now.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' })
}

onMounted(() => {
  updateClock()
  clockInterval = setInterval(updateClock, 1000)
  captionInterval = setInterval(() => {
    captionStep.value = (captionStep.value + 1) % captionLines.length
  }, 3000)
})

// ── Linterna del cursor sobre las tarjetas ─────────────────────────────────
// El resplandor redondo que sigue al ratón, como el «reveal» de Windows 10. La
// pintura es del todo CSS (el degradado radial de .home-feature-card::before);
// esto solo le dice DÓNDE está el cursor, en dos variables.
//
// Tres decisiones, por si alguien lo toca:
//  - Un oyente en la rejilla y no uno por tarjeta: la tarjeta de debajo se
//    resuelve con closest(), así que da igual cuántas tarjetas haya.
//  - Las variables se escriben en el elemento, no en un ref de Vue: un
//    pointermove dispara decenas de veces por segundo y no hace falta volver a
//    renderizar nada para mover un degradado.
//  - Una escritura por fotograma (requestAnimationFrame) y el rect se mide ahí
//    dentro, no en cada evento, para no encadenar lecturas de layout.
let glowFrame = 0
let glowPending: { card: HTMLElement; x: number; y: number } | null = null

const paintGlow = (): void => {
  glowFrame = 0
  if (!glowPending) return
  const { card, x, y } = glowPending
  const box = card.getBoundingClientRect()
  card.style.setProperty('--glow-x', `${x - box.left}px`)
  card.style.setProperty('--glow-y', `${y - box.top}px`)
}

const moveGlow = (event: PointerEvent): void => {
  // Sin ratón no hay linterna: en una pantalla táctil el dedo tapa justo lo que
  // iluminaría, y el CSS ya está detrás de @media (hover: hover).
  if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return
  const card = (event.target as HTMLElement | null)?.closest?.('.home-feature-card')
  if (!card) return
  glowPending = { card: card as HTMLElement, x: event.clientX, y: event.clientY }
  if (!glowFrame) glowFrame = requestAnimationFrame(paintGlow)
}

const dropGlow = (): void => {
  if (glowFrame) cancelAnimationFrame(glowFrame)
  glowFrame = 0
  glowPending = null
}

// La escena del logotipo gira un poco hacia el cursor. El oyente va en todo el
// hero (no solo en el lienzo) para que se mueva mientras se lee el texto; un
// frame por pintura, como la linterna de las tarjetas. Sin ratón, o pidiendo
// menos movimiento, se queda en su inclinación de reposo (la del CSS).
const heroVisual = ref<HTMLElement | null>(null)
const TILT_MAX_DEG = 7
let tiltFrame = 0
let tiltPending: { x: number; y: number } | null = null
const reduceMotion = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  ? window.matchMedia('(prefers-reduced-motion: reduce)')
  : null

const paintTilt = (): void => {
  tiltFrame = 0
  const stage = heroVisual.value
  if (!tiltPending || !stage) return
  const box = stage.getBoundingClientRect()
  if (!box.width || !box.height) return
  // -1…1 desde el centro del lienzo, recortado: lejos del lienzo no gira más.
  const clamp = (v: number): number => Math.max(-1, Math.min(1, v))
  const dx = clamp((tiltPending.x - (box.left + box.width / 2)) / box.width)
  const dy = clamp((tiltPending.y - (box.top + box.height / 2)) / box.height)
  stage.style.setProperty('--tilt-y', `${(dx * TILT_MAX_DEG).toFixed(2)}deg`)
  stage.style.setProperty('--tilt-x', `${(-dy * TILT_MAX_DEG).toFixed(2)}deg`)
}

const moveTilt = (event: PointerEvent): void => {
  if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return
  if (reduceMotion?.matches) return
  tiltPending = { x: event.clientX, y: event.clientY }
  if (!tiltFrame) tiltFrame = requestAnimationFrame(paintTilt)
}

const dropTilt = (): void => {
  if (tiltFrame) cancelAnimationFrame(tiltFrame)
  tiltFrame = 0
  tiltPending = null
  heroVisual.value?.style.removeProperty('--tilt-x')
  heroVisual.value?.style.removeProperty('--tilt-y')
}

onUnmounted(() => {
  if (clockInterval) clearInterval(clockInterval)
  if (captionInterval) clearInterval(captionInterval)
  dropGlow()
  dropTilt()
})
</script>

<style scoped>
.home-page {
  /* La geometría de la página, en un solo sitio: la barra, el hero y la rejilla
     de tarjetas se alinean porque usan ESTOS dos valores y no los suyos. Antes
     cada bloque repetía su `max-width` y su padding, y bastaba tocar uno para
     que los bordes dejaran de caer a plomo. */
  --home-max: 1200px;
  --home-gutter: 48px;

  min-height: 100vh;
  background: #202124;
  font-family: 'Google Sans', 'Segoe UI', Roboto, sans-serif;
  color: #e8eaed;
  display: flex;
  flex-direction: column;
}

/* La primera pantalla: lo que se ve sin tocar la rueda del ratón. `dvh` para
   que en un móvil cuente el alto REAL (la barra del navegador se esconde al
   bajar y con `vh` la pantalla quedaba cortada); `vh` antes, de reserva, para
   quien no entienda `dvh`. El hero ya era `flex: 1`, así que se come todo lo que
   la barra deje libre sin número mágico ninguno. */
.home-first-screen {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

/* Top Bar */
.home-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* Mismo ancho y mismo retranqueo que el hero y las tarjetas: el logotipo de la
     barra cae a plomo sobre el título y sobre el borde de la primera tarjeta. A
     sangre (como estaba) eso solo se cumplía en una ventana estrecha: en cuanto
     la página pasaba de 1200 px, el hero se quedaba centrado y la barra no. */
  max-width: var(--home-max);
  width: 100%;
  margin: 0 auto;
  padding: 22px var(--home-gutter);
  flex-shrink: 0;
}

.home-topbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.home-topbar-title {
  font-size: 18px;
  font-weight: 500;
  color: #e8eaed;
}

.home-topbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.home-topbar-time {
  font-size: 14px;
  color: #9aa0a6;
}

/* Hero */
.home-hero {
  display: flex;
  align-items: center;
  /* `space-between` y no `center`: el texto mide como mucho 520 y el lienzo 420,
     así que centrados dejaban ~50 px de aire a cada lado y el título arrancaba a
     la derecha del borde de la primera tarjeta. Así el texto empieza donde
     empieza la rejilla y el lienzo acaba donde acaba. */
  justify-content: space-between;
  padding: 60px var(--home-gutter) 80px;
  gap: 64px;
  flex: 1;
  max-width: var(--home-max);
  margin: 0 auto;
  width: 100%;
}

.home-hero-content {
  max-width: 520px;
}

.home-hero-title {
  font-size: 48px;
  font-weight: 400;
  line-height: 1.2;
  margin: 0 0 20px;
  color: #e8eaed;
}

.home-hero-subtitle {
  font-size: 18px;
  line-height: 1.6;
  color: #9aa0a6;
  margin: 0 0 40px;
}

.home-hero-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.home-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: #8ab4f8;
  color: #202124;
  border-radius: 24px;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  font-family: inherit;
  transition: background 0.2s;
}

.home-btn-primary:hover {
  background: #aecbfa;
}

.home-btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: transparent;
  color: #8ab4f8;
  border: 1px solid #8ab4f8;
  border-radius: 24px;
  text-decoration: none;
  font-weight: 500;
  font-size: 15px;
  font-family: inherit;
  transition: background 0.2s;
}

.home-btn-secondary:hover {
  background: rgba(138, 180, 248, 0.08);
}

.home-btn-icon {
  width: 20px;
  height: 20px;
}

/* El lienzo del logotipo y de las maquetas. Tiene tamaño propio (el logotipo
   son 280 y las maquetas se salen de él a propósito) y es el ancla de los
   posicionamientos absolutos de abajo. */
.home-hero-visual {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 420px;
  height: 420px;
  /* En 3D: el lienzo entero es una escena en perspectiva, inclinada en reposo
     hacia el texto, y cada maqueta flota a su propia profundidad (translateZ
     abajo). El cursor suma su giro a esa inclinación (--tilt-x/--tilt-y, ver
     moveTilt); al salir vuelve despacio. */
  --tilt-x: 0deg;
  --tilt-y: 0deg;
  transform-style: preserve-3d;
  transform: perspective(1100px)
    rotateX(calc(6deg + var(--tilt-x)))
    rotateY(calc(-12deg + var(--tilt-y)));
  transition: transform 0.6s cubic-bezier(0.2, 0.7, 0.2, 1);
}

/* El logotipo, al fondo de la escena: las maquetas pasan por delante. */
.home-hero-logo {
  transform: translateZ(-40px);
}

/* ── Maquetas del hero ──────────────────────────────────────────────────────
   Dos tarjetas flotando sobre el logotipo, giradas lo justo para que no
   parezcan un pantallazo pegado. Flotan muy despacio y en contrafase. */
.home-mock {
  position: absolute;
  /* Luz desde arriba: la cara más clara arriba, el borde superior encendido y
     el inferior en sombra. Las sombras apiladas de 1 px son el canto de la
     tarjeta (su grosor); la grande y difusa, lo que proyecta sobre el lienzo. */
  background: linear-gradient(165deg, #323337 0%, #292a2d 55%, #242528 100%);
  border: 1px solid #3c4043;
  border-top-color: #55585d;
  border-bottom-color: #2a2b2e;
  border-radius: 14px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 1px 0 #202124,
    0 2px 0 #1d1e21,
    0 3px 0 #1b1c1e,
    0 4px 0 #18191b,
    0 30px 50px -12px rgba(0, 0, 0, 0.65),
    0 12px 24px rgba(0, 0, 0, 0.3);
  transform-style: preserve-3d;
  animation: home-mock-float 7s ease-in-out infinite;
}

/* El reflejo: una franja de luz que se desplaza con la inclinación de la
   escena, como si las tarjetas fueran de cristal mate. */
.home-mock:not(.home-mock--caption)::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(
    115deg,
    transparent 30%,
    rgba(255, 255, 255, 0.07) 45%,
    transparent 60%
  );
  background-size: 250% 100%;
  background-position: calc(50% - var(--tilt-y) * 6) 0;
  transition: background-position 0.6s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.home-mock--live {
  top: 18px;
  left: -26px;
  width: 232px;
  padding: 12px;
  transform: translateZ(70px) rotateY(10deg) rotate(-3deg);
}

.home-mock--rec {
  bottom: 26px;
  right: -34px;
  width: 266px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  transform: translateZ(55px) rotateY(-8deg) rotate(2deg);
  animation-delay: -3.5s;
}

/* Pantalla compartida: arriba a la derecha, por fuera del logotipo. */
.home-mock--screen {
  top: 96px;
  right: -40px;
  width: 178px;
  padding: 10px;
  transform: translateZ(35px) rotateY(-12deg) rotate(2.5deg);
  animation-delay: -1.8s;
}

/* Pizarra: abajo a la izquierda, cerrando el corro. */
.home-mock--board {
  bottom: 96px;
  left: -40px;
  width: 166px;
  padding: 10px;
  transform: translateZ(25px) rotateY(12deg) rotate(-2deg);
  animation-delay: -5.2s;
}

/* La tira de subtítulo: en el medio a la derecha, entre la pantalla compartida
   y el reproductor. Ancho fijo y alto mínimo a propósito — la misma frase ocupa
   distinto en cada idioma, y sin eso la tira cambiaría de tamaño cada 3 s. */
.home-mock--caption {
  top: 247px;
  background: transparent;
  border: none;
  right: -10px;
  box-shadow: none;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  transform: translateZ(95px) rotateY(-6deg) rotate(1.5deg);
  animation-delay: -2.6s;
}

.home-mock-cc {
  font-style: normal;
  font-size: 9px;
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 4px 5px;
  border-radius: 4px;
  background: rgba(138, 180, 248, 0.16);
  color: #8ab4f8;
}

.home-mock-caption-text {
  flex: 1;
  min-height: 30px;
  font-size: 1.75rem;
  text-shadow: 0px 0px 12px rgba(200, 200, 200, 0.5);
  line-height: 1.35;
  color: #e8eaed;
}

/* El relevo entre idiomas: se va la frase, entra la siguiente. `out-in` para que
   nunca haya dos a la vez (se solaparían dentro de la tira). */
.home-caption-enter-active,
.home-caption-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.home-caption-enter-from {
  opacity: 0;
  transform: translateY(5px);
}

.home-caption-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

@keyframes home-mock-float {
  0%, 100% {
    translate: 0 0;
  }
  50% {
    translate: 0 -9px;
  }
}

/* La cabecera de la sesión: «en directo» con su punto latiendo, y el REC. */
.home-mock-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.home-mock-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #81c995;
  background: rgba(129, 201, 149, 0.14);
  padding: 3px 8px;
  border-radius: 999px;
}

.home-mock-pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #81c995;
  animation: home-mock-pulse 1.8s ease-in-out infinite;
}

@keyframes home-mock-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.25;
  }
}

.home-mock-rec {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: #f28b82;
}

/* Los cuatro mosaicos de cámara: iniciales sobre un fondo de su color. */
.home-mock-tiles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.home-mock-tile {
  height: 54px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  color: #e8eaed;
}

.home-mock-tile--blue {
  background: rgba(138, 180, 248, 0.18);
}

.home-mock-tile--green {
  background: rgba(129, 201, 149, 0.18);
}

.home-mock-tile--purple {
  background: rgba(197, 138, 249, 0.18);
}

.home-mock-tile--amber {
  background: rgba(253, 214, 99, 0.16);
  color: #9aa0a6;
}

/* La grabación publicada: play, su título insinuado, duración e idiomas. */
.home-mock-play {
  width: 26px;
  height: 26px;
  min-width: 26px;
  border-radius: 50%;
  background: rgba(138, 180, 248, 0.18);
  color: #8ab4f8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.home-mock-play-icon {
  width: 12px;
  height: 12px;
}

.home-mock-lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.home-mock-line {
  height: 6px;
  border-radius: 3px;
  background: #3c4043;
}

.home-mock-line--short {
  width: 55%;
}

.home-mock-time {
  font-size: 11px;
  color: #9aa0a6;
  font-variant-numeric: tabular-nums;
}

.home-mock-langs {
  display: flex;
  gap: 3px;
}

.home-mock-langs i {
  font-style: normal;
  font-size: 9px;
  line-height: 1;
  padding: 3px 4px;
  border-radius: 4px;
  background: #35363a;
  color: #9aa0a6;
}

/* La ventana compartida: su barra de tres puntos y lo que se proyecta. */
.home-mock-window {
  border-radius: 8px;
  background: #202124;
  border: 1px solid #3c4043;
  overflow: hidden;
}

.home-mock-window-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 7px;
  background: #35363a;
}

.home-mock-window-bar i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #5f6368;
}

.home-mock-window-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 9px 14px;
}

.home-mock-line--tiny {
  width: 32%;
}

/* La pizarra: lienzo claro y el trazo encima. */
.home-mock-board-canvas {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px;
  background: rgba(232, 234, 237, 0.06);
  border: 1px solid #3c4043;
  padding: 4px 0;
  box-sizing: border-box;
}

/* El trazo se dibuja, se queda un momento y vuelve a empezar. El largo lo
   normaliza el atributo pathLength="1" del propio elemento (en CSS no existe esa
   propiedad), así que aquí basta con ir de 1 a 0. */
.home-mock-stroke {
  /* tiene que estar en un número un poquittitio superior a 1 porq muestra un punto random al otro lado */
  stroke-dasharray: 1.001;
  stroke-dashoffset: 1;
  animation: home-mock-draw 5s ease-in-out alternate infinite;
}

@keyframes home-mock-draw {
  from {
    stroke-dashoffset: 1;
  }
  to {
    stroke-dashoffset: 0;
  }
}

/* El distintivo de cada maqueta: el icono de lo que está pasando. */
.home-mock-chip {
  position: absolute;
  right: -8px;
  bottom: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #3c4043;
  background: #292a2d;
}

.home-mock-chip-icon {
  width: 12px;
  height: 12px;
}

.home-mock-chip--cyan {
  color: #78d9ec;
}

.home-mock-chip--green {
  color: #81c995;
}

/* Quien pide menos movimiento se queda con las maquetas quietas: ni flotación,
   ni punto latiendo, ni trazo dibujándose (y el trazo, entero). */
@media (prefers-reduced-motion: reduce) {
  .home-mock,
  .home-mock-pulse,
  .home-mock-stroke {
    animation: none;
  }

  /* La escena sigue en 3D, pero quieta (moveTilt tampoco la mueve). */
  .home-hero-visual,
  .home-mock::after {
    transition: none;
  }

  .home-mock-stroke {
    stroke-dashoffset: 0;
  }

  /* El idioma sigue cambiando —es lo que cuenta la tira— pero sin el salto:
     solo se desvanece. */
  .home-caption-enter-from,
  .home-caption-leave-to {
    transform: none;
  }
}

/* Features */
/* Seis tarjetas, 3 + 3. Era una fila flex de tres; con seis en la misma fila
   quedaban demasiado estrechas, así que es una rejilla de tres columnas que se
   parte en dos filas (y en dos columnas, y en una, según el ancho). */
.home-features {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  padding: 0 var(--home-gutter) 64px;
  max-width: var(--home-max);
  margin: 0 auto;
  width: 100%;
}

.home-feature-card {
  background: #292a2d;
  border: 1px solid #3c4043;
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  transition: border-color 0.2s;
  /* Para la linterna: `relative` la ancla a la tarjeta e `isolate` hace de la
     tarjeta un contexto de apilado, que es lo que deja el resplandor (z-index
     -1) por encima del fondo de la tarjeta y por debajo de su texto. Sin
     `overflow: hidden` a propósito: recortaría el tooltip del asterisco de
     «Vídeo HD», que se dibuja por encima del borde (ver styles/tooltip.css). El
     círculo no se sale porque hereda el redondeo. */
  position: relative;
  isolation: isolate;
}

.home-feature-card:hover {
  border-color: #5f6368;
}

/* La linterna. El degradado es el «círculo difuminado»: un radial-gradient se
   desvanece solo, que es más barato que un filter: blur() siguiendo al cursor.
   Las coordenadas llegan en --glow-x/--glow-y (ver moveGlow en el script); el
   50% de reserva es para que, si alguna vez se enseña sin que el ratón se haya
   movido, el resplandor salga en el centro y no en la esquina. */
.home-feature-card::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.25s ease;
  background: radial-gradient(
    circle 180px at var(--glow-x, 50%) var(--glow-y, 50%),
    rgba(232, 234, 237, 0.10),
    rgba(232, 234, 237, 0.045) 45%,
    transparent 70%
  );
}

/* Solo donde hay puntero de verdad: en una pantalla táctil no hay «pasar por
   encima», y el resplandor se quedaría encendido tras cada toque. */
@media (hover: hover) {
  .home-feature-card:hover::before {
    opacity: 1;
  }
}

/* Quien pide menos movimiento no quiere una luz persiguiéndole el cursor. */
@media (prefers-reduced-motion: reduce) {
  .home-feature-card::before {
    display: none;
  }
}

.home-feature-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.home-feature-icon--blue {
  background: rgba(138, 180, 248, 0.15);
  color: #8ab4f8;
}

.home-feature-icon--green {
  background: rgba(129, 201, 149, 0.15);
  color: #81c995;
}

.home-feature-icon--purple {
  background: rgba(197, 138, 249, 0.15);
  color: #c58af9;
}

.home-feature-icon--amber {
  background: rgba(253, 214, 99, 0.15);
  color: #fdd663;
}

.home-feature-icon--red {
  background: rgba(242, 139, 130, 0.15);
  color: #f28b82;
}

.home-feature-icon--cyan {
  background: rgba(120, 217, 236, 0.15);
  color: #78d9ec;
}

.home-icon {
  width: 24px;
  height: 24px;
}

.home-feature-title {
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 8px;
  color: #e8eaed;
}

.home-feature-desc {
  font-size: 14px;
  color: #9aa0a6;
  line-height: 1.5;
  margin: 0;
}

/* Footer */
.home-footer {
  text-align: center;
  padding: 32px 24px;
  color: #5f6368;
  font-size: 13px;
}

.home-footer p {
  margin: 0;
}

/* Responsive */
@media (max-width: 1024px) {
  /* El retranqueo baja para los tres bloques a la vez, así que siguen alineados
     también aquí. */
  .home-page {
    --home-gutter: 24px;
  }

  .home-features {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .home-hero {
    flex-direction: column;
    text-align: center;
    /* En columna el eje principal es el vertical: con `space-between` el texto
       se pegaría arriba del hero a pantalla completa en vez de quedar centrado. */
    justify-content: center;
    padding: 40px var(--home-gutter) 60px;
    gap: 40px;
  }

  .home-hero-actions {
    justify-content: center;
  }

  /* Por debajo de esto el hero es una columna y el lienzo del logotipo (con sus
     maquetas) no cabe al lado del texto: se va entero, como ya hacía. */
  .home-hero-visual {
    display: none;
  }
}

@media (max-width: 600px) {
  .home-hero-title {
    font-size: 32px;
  }

  .home-hero-subtitle {
    font-size: 16px;
  }

  .home-hero-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .home-btn-primary,
  .home-btn-secondary {
    justify-content: center;
  }

  .home-features {
    grid-template-columns: 1fr;
    padding-bottom: 48px;
  }

  /* Solo el alto: el lateral ya lo pone --home-gutter, igual para los tres. */
  .home-topbar {
    padding-block: 16px;
  }
}
</style>
